import { useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import { useCollection } from "../../hooks/useCollection";

const ICON_OPTIONS = [
  "FaDribbble",
  "FaFileAlt",
  "FaTachometerAlt",
  "FaGlobe",
  "FaLeaf",
  "FaFlask",
  "FaGraduationCap",
  "FaHandshake",
  "FaUsers",
  "FaBookOpen",
];

const emptyForm = { icon: "FaDribbble", title: "", description: "", order: 0 };

export default function ServicesAdmin() {
  const { items, loading } = useCollection("services", "order", "asc");
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const reset = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      icon: item.icon || "FaDribbble",
      title: item.title || "",
      description: item.description || "",
      order: item.order ?? 0,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        order: Number(form.order) || 0,
        updatedAt: serverTimestamp(),
      };
      if (editingId) {
        await updateDoc(doc(db, "services", editingId), payload);
      } else {
        await addDoc(collection(db, "services"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
      }
      reset();
    } catch (err) {
      alert(`บันทึกไม่สำเร็จ: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`ลบ "${item.title}"?`)) return;
    await deleteDoc(doc(db, "services", item.id));
  };

  return (
    <div>
      <h2 className="mb-4">จัดการ Activities (กิจกรรม/บริการ)</h2>

      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <h5 className="card-title mb-3">
            {editingId ? "แก้ไขกิจกรรม" : "เพิ่มกิจกรรมใหม่"}
          </h5>
          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label">Icon</label>
                <select
                  className="form-select"
                  value={form.icon}
                  onChange={update("icon")}
                >
                  {ICON_OPTIONS.map((i) => (
                    <option key={i} value={i}>
                      {i}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">ชื่อกิจกรรม</label>
                <input
                  className="form-control"
                  value={form.title}
                  onChange={update("title")}
                  required
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">ลำดับ</label>
                <input
                  type="number"
                  className="form-control"
                  value={form.order}
                  onChange={update("order")}
                />
              </div>
              <div className="col-12">
                <label className="form-label">คำอธิบาย</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={form.description}
                  onChange={update("description")}
                  required
                />
              </div>
            </div>
            <div className="mt-3 d-flex gap-2">
              <button className="btn btn-primary" disabled={saving}>
                {saving ? "กำลังบันทึก..." : editingId ? "บันทึก" : "เพิ่ม"}
              </button>
              {editingId && (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={reset}
                >
                  ยกเลิก
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <h5 className="card-title mb-3">รายการ ({items.length})</h5>
          {loading ? (
            <div>กำลังโหลด...</div>
          ) : items.length === 0 ? (
            <div className="text-muted">ยังไม่มีรายการ</div>
          ) : (
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>Icon</th>
                  <th>ชื่อ</th>
                  <th>คำอธิบาย</th>
                  <th style={{ width: 80 }}>ลำดับ</th>
                  <th style={{ width: 180 }}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td><code>{item.icon}</code></td>
                    <td className="fw-semibold">{item.title}</td>
                    <td className="small text-muted">{item.description}</td>
                    <td>{item.order ?? 0}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => handleEdit(item)}
                      >
                        แก้ไข
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(item)}
                      >
                        ลบ
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
